import axios from "api/axios"
import { Button } from "components/button"
import { Input } from "components/input"
import React from "react"
import { FaAsterisk } from "react-icons/fa"
import {
  RiArrowDownSLine,
  RiArrowRightSLine,
  RiArrowUpDownLine,
  RiArrowUpSLine,
  RiSearchLine,
} from "react-icons/ri"
import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import styled from "styled-components"

export type TAppointmentTypeDefault = {
  id: number
  label?: string | null
  fk_appointment_type_id: number
}

export type TAppointmentTypeWithDefaults = {
  id: number
  label: string
  defaults?: TAppointmentTypeDefault[]
}

/** Row from API / picker (always persisted template id). */
export type TChecklistPickerRow = {
  id: number
  label: string | null
  order: number | null
  type: string | null
  required: boolean | null
  fk_appointment_type_id: number | null
  fk_sub_appointment_type_id: number | null
}

export type ChecklistExistingPickerVariant = "admin" | "appointment"

export type ChecklistExistingPickerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  anchorRef: React.RefObject<HTMLElement | null>
  appointmentTypeId: number
  subAppointmentTypeId?: number | null
  /** Distinguishes call sites; both list every company checklist template. */
  variant: ChecklistExistingPickerVariant
  onConfirm: (rows: TChecklistPickerRow[]) => void
  /** Optional i18n; defaults to `components.checklist_dropdown.*` */
  translate?: (key: string, options?: Record<string, string | number>) => string
}

type ExistingSortKey = "label" | "order" | "required" | "from"
type ExistingSortState = { key: ExistingSortKey; dir: "asc" | "desc" }

const EXISTING_PICKER_GRID = "2rem minmax(0, 0.42fr) 2.375rem minmax(0, 1fr) 2.75rem"

type ExistingSortColumnDef = {
  key: ExistingSortKey
  ariaKey: string
  labelKey?: string
  headerVariant?: "text" | "hash" | "requiredIcon"
}

const EXISTING_SORT_COLUMN_DEFS: ExistingSortColumnDef[] = [
  { key: "from", labelKey: "From", ariaKey: "Sort by source", headerVariant: "text" },
  { key: "order", ariaKey: "Sort by order", headerVariant: "hash" },
  { key: "label", labelKey: "Label", ariaKey: "Sort by label", headerVariant: "text" },
  { key: "required", ariaKey: "Sort by required", headerVariant: "requiredIcon" },
]

const UNASSIGNED_APPOINTMENT_TYPE_GROUP = -1

const getSourceGroupIdentity = (row: TChecklistPickerRow) => {
  const parentRaw = row.fk_appointment_type_id
  const subRaw = row.fk_sub_appointment_type_id
  const parentId =
    parentRaw != null && Number.isFinite(Number(parentRaw))
      ? Number(parentRaw)
      : UNASSIGNED_APPOINTMENT_TYPE_GROUP
  const subId = subRaw != null && Number.isFinite(Number(subRaw)) ? Number(subRaw) : null
  return {
    parentId,
    subId,
    key: `${parentId}:${subId ?? "parent"}`,
  }
}

const sortPickerRows = (rows: TChecklistPickerRow[]) =>
  [...rows].sort((a, b) => {
    const la = (a.label || "").toLowerCase()
    const lb = (b.label || "").toLowerCase()
    if (la !== lb) return la.localeCompare(lb)
    return Number(a.id) - Number(b.id)
  })

const buildAllPickerCandidates = (all: TChecklistPickerRow[]): TChecklistPickerRow[] =>
  sortPickerRows(all.filter((row) => row.id != null && Number.isFinite(Number(row.id))))

export const ChecklistExistingPicker = ({
  open,
  onOpenChange,
  anchorRef,
  appointmentTypeId,
  subAppointmentTypeId = null,
  variant: _variant,
  onConfirm,
  translate: translateProp,
}: ChecklistExistingPickerProps) => {
  const { t } = useTranslation()
  const tr = React.useCallback(
    (key: string, options?: Record<string, string | number>) =>
      translateProp
        ? translateProp(key, options)
        : t(`components.checklist_dropdown.${key}`, options),
    [t, translateProp]
  )

  const existingSortColumns = React.useMemo(
    () =>
      EXISTING_SORT_COLUMN_DEFS.map((def) => ({
        key: def.key,
        headerVariant: def.headerVariant ?? "text",
        label: def.labelKey ? tr(def.labelKey) : "",
        ariaLabel: tr(def.ariaKey),
      })),
    [tr]
  )

  const [existingSearch, setExistingSearch] = React.useState("")
  const [existingSort, setExistingSort] = React.useState<ExistingSortState>({
    key: "from",
    dir: "asc",
  })
  const [selectedExistingIds, setSelectedExistingIds] = React.useState<number[]>([])
  const [expandedExistingGroups, setExpandedExistingGroups] = React.useState<
    Record<string, boolean>
  >({})

  const appointmentTypesQuery = useQuery<TAppointmentTypeWithDefaults[]>(
    "appointment-types-with-defaults-checklist-labels",
    async () =>
      await axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/appointment-types/with-defaults`)
        .then((res) => res.data || []),
    { refetchOnWindowFocus: false }
  )

  const checklistsQuery = useQuery<TChecklistPickerRow[]>(
    ["admin-checklists", appointmentTypeId, subAppointmentTypeId ?? null],
    async () => {
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/checklist`
      )
      const raw = Array.isArray(res.data) ? res.data : []
      return raw.map((row: TChecklistPickerRow) => ({
        ...row,
        required: row.required === true ? true : row.required === false ? false : null,
      }))
    },
    { refetchOnWindowFocus: false }
  )

  const existingCopyCandidates = React.useMemo(
    () => buildAllPickerCandidates(checklistsQuery.data ?? []),
    [checklistsQuery.data]
  )

  const getSourceContextLabel = React.useCallback(
    (row: TChecklistPickerRow) => {
      if (row.fk_appointment_type_id === null || row.fk_appointment_type_id === undefined) {
        return tr("Unassigned")
      }
      const types = appointmentTypesQuery.data || []
      const parent = types.find((ty) => Number(ty.id) === Number(row.fk_appointment_type_id))
      const parentLabel =
        parent?.label ||
        tr("Appointment type #{{id}}", { id: String(row.fk_appointment_type_id) })
      if (row.fk_sub_appointment_type_id === null || row.fk_sub_appointment_type_id === undefined) {
        return parentLabel
      }
      const sub = parent?.defaults?.find(
        (d) => Number(d.id) === Number(row.fk_sub_appointment_type_id)
      )
      const subLabel =
        sub?.label || tr("Sub type #{{id}}", { id: String(row.fk_sub_appointment_type_id) })
      return `${parentLabel} > ${subLabel}`
    },
    [appointmentTypesQuery.data, tr]
  )

  const searchFilteredRows = React.useMemo(() => {
    const q = existingSearch.trim().toLowerCase()
    let rows = existingCopyCandidates
    if (q) {
      rows = rows.filter((row) => {
        const fromLabel = getSourceContextLabel(row).toLowerCase()
        const haystack = [(row.label || "").toLowerCase(), fromLabel]
        return haystack.some((chunk) => chunk.includes(q))
      })
    }
    return rows
  }, [existingCopyCandidates, existingSearch, getSourceContextLabel])

  /** Flat row order for label / order / required. When sorting by From, grouping handles order. */
  const flatSortedPickerRows = React.useMemo(() => {
    if (existingSort.key === "from") {
      return [...searchFilteredRows]
    }
    const dirMult = existingSort.dir === "asc" ? 1 : -1
    const tie = (a: TChecklistPickerRow, b: TChecklistPickerRow) =>
      (Number(a.id) - Number(b.id)) * dirMult
    const cmpOrder = (a: TChecklistPickerRow, b: TChecklistPickerRow) => {
      const na = a.order == null ? null : Number(a.order)
      const nb = b.order == null ? null : Number(b.order)
      const aBad = na == null || !Number.isFinite(na)
      const bBad = nb == null || !Number.isFinite(nb)
      if (aBad && bBad) return 0
      if (aBad) return 1
      if (bBad) return -1
      return (na - nb) * dirMult
    }
    const cmpRequired = (a: TChecklistPickerRow, b: TChecklistPickerRow) => {
      const ra = a.required === true ? 1 : 0
      const rb = b.required === true ? 1 : 0
      return (ra - rb) * dirMult
    }
    return [...searchFilteredRows].sort((a, b) => {
      let primary = 0
      if (existingSort.key === "label") {
        primary =
          (a.label || "").localeCompare(b.label || "", undefined, { sensitivity: "base" }) *
          dirMult
      } else if (existingSort.key === "order") {
        primary = cmpOrder(a, b)
      } else {
        primary = cmpRequired(a, b)
      }
      if (primary !== 0) return primary
      return tie(a, b)
    })
  }, [searchFilteredRows, existingSort.dir, existingSort.key])

  const toggleExistingSort = React.useCallback((key: ExistingSortKey) => {
    setExistingSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    )
  }, [])

  React.useEffect(() => {
    if (!open) {
      setExistingSearch("")
      setSelectedExistingIds([])
      setExpandedExistingGroups({})
    }
  }, [open])

  React.useEffect(() => {
    if (!open) return
    const onMouseDown = (event: MouseEvent) => {
      const node = anchorRef.current
      if (node && !node.contains(event.target as Node)) {
        onOpenChange(false)
      }
    }
    document.addEventListener("mousedown", onMouseDown)
    return () => document.removeEventListener("mousedown", onMouseDown)
  }, [open, anchorRef, onOpenChange])

  const groupedExistingPickerRows = React.useMemo(() => {
    const sourceRows =
      existingSort.key === "from" ? searchFilteredRows : flatSortedPickerRows

    const map = new Map<
      string,
      { groupKey: string; sortParentId: number; title: string; rows: TChecklistPickerRow[] }
    >()
    for (const row of sourceRows) {
      const identity = getSourceGroupIdentity(row)
      if (!map.has(identity.key)) {
        const title =
          identity.parentId === UNASSIGNED_APPOINTMENT_TYPE_GROUP
            ? tr("Unassigned")
            : getSourceContextLabel(row)
        map.set(identity.key, {
          groupKey: identity.key,
          sortParentId: identity.parentId,
          title,
          rows: [],
        })
      }
      map.get(identity.key)!.rows.push(row)
    }
    let groups = [...map.values()]
    const sortRowsByOrderAsc = (rows: TChecklistPickerRow[]) =>
      [...rows].sort((a, b) => {
        const na = a.order == null ? null : Number(a.order)
        const nb = b.order == null ? null : Number(b.order)
        const aBad = na == null || !Number.isFinite(na)
        const bBad = nb == null || !Number.isFinite(nb)
        if (aBad && bBad) return Number(a.id) - Number(b.id)
        if (aBad) return 1
        if (bBad) return -1
        if (na !== nb) return na - nb
        return Number(a.id) - Number(b.id)
      })

    if (existingSort.key === "from") {
      const dirMult = existingSort.dir === "asc" ? 1 : -1
      groups.sort((a, b) => {
        const c =
          a.title.localeCompare(b.title, undefined, { sensitivity: "base" }) * dirMult
        if (c !== 0) return c
        if (a.sortParentId !== b.sortParentId) return a.sortParentId - b.sortParentId
        return a.groupKey.localeCompare(b.groupKey)
      })
    } else {
      const groupFirstIndex = new Map<string, number>()
      let idx = 0
      for (const row of sourceRows) {
        const identity = getSourceGroupIdentity(row)
        if (!groupFirstIndex.has(identity.key)) {
          groupFirstIndex.set(identity.key, idx)
          idx += 1
        }
      }
      groups.sort(
        (a, b) =>
          (groupFirstIndex.get(a.groupKey) ?? 0) - (groupFirstIndex.get(b.groupKey) ?? 0)
      )
    }

    return groups.map((g) => ({
      ...g,
      rows: sortRowsByOrderAsc(g.rows),
    }))
  }, [
    searchFilteredRows,
    flatSortedPickerRows,
    existingSort.key,
    existingSort.dir,
    getSourceContextLabel,
    tr,
  ])

  const toggleExistingRowSelected = (checklistId: number) => {
    setSelectedExistingIds((prev) =>
      prev.includes(checklistId) ? prev.filter((id) => id !== checklistId) : [...prev, checklistId]
    )
  }

  const toggleGroupExpanded = (groupKey: string) => {
    const gKey = String(groupKey)
    setExpandedExistingGroups((prev) => ({ ...prev, [gKey]: !prev[gKey] }))
  }

  const toggleGroupSelectAll = (group: { rows: TChecklistPickerRow[] }) => {
    const rowIds = group.rows.map((r) => r.id).filter((id): id is number => id != null)
    if (rowIds.length === 0) return
    setSelectedExistingIds((ids) => {
      const allSelected = rowIds.every((id) => ids.includes(id))
      if (allSelected) {
        return ids.filter((id) => !rowIds.includes(id))
      }
      return [...new Set([...ids, ...rowIds])]
    })
  }

  const handleConfirmSelectedExisting = () => {
    const idSet = new Set(selectedExistingIds)
    const sources = groupedExistingPickerRows
      .flatMap((g) => g.rows)
      .filter((r) => idSet.has(r.id))
    onConfirm(sources)
    setSelectedExistingIds([])
    onOpenChange(false)
  }

  if (!open) {
    return null
  }

  return (
    <SExistingPanel>
      <SExistingSearchRow>
        <div className="flex items-center gap-2">
          <RiSearchLine size={16} />
          <Input
            value={existingSearch}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setExistingSearch(event.target.value)
            }
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === "Enter") {
                event.preventDefault()
              }
            }}
            placeholder={tr("Search label or source…")}
            name="existing-checklist-search"
            style={{
              width: "100%",
              borderRadius: "0.375rem",
              fontSize: "0.8125rem",
            }}
          />
          <Button
            primary
            type="button"
            disabled={selectedExistingIds.length < 1}
            onClick={handleConfirmSelectedExisting}
          >
            {tr("Add with count", { count: selectedExistingIds.length })}
          </Button>
          <Button type="button" onClick={() => onOpenChange(false)}>
            {tr("Close")}
          </Button>
        </div>
      </SExistingSearchRow>
      <SExistingHeaderRow>
        <SExistingHeaderSpacer aria-hidden />
        {existingSortColumns.map((col) => {
          const center = col.headerVariant === "hash" || col.headerVariant === "requiredIcon"
          return (
            <SExistingHeaderSortCell key={col.key} $center={center}>
              <SExistingHeaderTitleWithSort $center={center}>
                <SExistingHeaderLabel $iconOnly={col.headerVariant === "requiredIcon"}>
                  {col.headerVariant === "hash" ? (
                    "#"
                  ) : col.headerVariant === "requiredIcon" ? (
                    <FaAsterisk size={12} aria-hidden />
                  ) : (
                    col.label
                  )}
                </SExistingHeaderLabel>
                <SExistingSortToggle
                  type="button"
                  onClick={() => toggleExistingSort(col.key)}
                  aria-label={col.ariaLabel}
                  $active={existingSort.key === col.key}
                >
                  {existingSort.key === col.key ? (
                    existingSort.dir === "asc" ? (
                      <RiArrowUpSLine size={16} />
                    ) : (
                      <RiArrowDownSLine size={16} />
                    )
                  ) : (
                    <RiArrowUpDownLine size={16} />
                  )}
                </SExistingSortToggle>
              </SExistingHeaderTitleWithSort>
            </SExistingHeaderSortCell>
          )
        })}
      </SExistingHeaderRow>
      <SExistingBody>
        {existingCopyCandidates.length === 0 ? (
          <SExistingEmpty>{tr("No checklist items")}</SExistingEmpty>
        ) : searchFilteredRows.length === 0 ? (
          <SExistingEmpty>{tr("No matches for your search")}</SExistingEmpty>
        ) : (
          groupedExistingPickerRows.map((group) => {
            const gKey = String(group.groupKey)
            const isOpenGroup = !!expandedExistingGroups[gKey]
            const rowIds = group.rows
              .map((r) => r.id)
              .filter((id): id is number => id != null)
            const selectedInGroup = rowIds.filter((id) => selectedExistingIds.includes(id))
            const allInGroupSelected =
              rowIds.length > 0 && selectedInGroup.length === rowIds.length
            const someInGroupSelected =
              selectedInGroup.length > 0 && selectedInGroup.length < rowIds.length
            return (
              <div key={gKey}>
                <SExistingGroupHeaderRow>
                  <SExistingGroupChevron
                    type="button"
                    aria-expanded={isOpenGroup}
                    aria-label={isOpenGroup ? tr("Collapse group") : tr("Expand group")}
                    onClick={() => toggleGroupExpanded(group.groupKey)}
                  >
                    {isOpenGroup ? (
                      <RiArrowDownSLine size={18} aria-hidden />
                    ) : (
                      <RiArrowRightSLine size={18} aria-hidden />
                    )}
                  </SExistingGroupChevron>
                  <input
                    type="checkbox"
                    className="shrink-0"
                    disabled={rowIds.length === 0}
                    aria-label={tr("Select all in group", { title: group.title })}
                    checked={allInGroupSelected}
                    ref={(el) => {
                      if (el) {
                        el.indeterminate = someInGroupSelected
                      }
                    }}
                    onChange={() => toggleGroupSelectAll(group)}
                  />
                  <span className="min-w-0 truncate font-medium text-left flex-1">
                    {group.title}
                  </span>
                  <span className="text-xs text-gray-500 shrink-0">({group.rows.length})</span>
                </SExistingGroupHeaderRow>
                {isOpenGroup
                  ? group.rows.map((row) => (
                      <SExistingDataRow key={row.id}>
                        <SExistingCell $center>
                          <input
                            type="checkbox"
                            checked={selectedExistingIds.includes(row.id)}
                            onChange={() => toggleExistingRowSelected(row.id)}
                            aria-label={tr("Select checklist row")}
                          />
                        </SExistingCell>
                        <SExistingCell title={getSourceContextLabel(row)}>
                          {getSourceContextLabel(row)}
                        </SExistingCell>
                        <SExistingCell $center title={row.order != null ? String(row.order) : ""}>
                          {row.order ?? "—"}
                        </SExistingCell>
                        <SExistingCell title={row.label || ""}>{row.label || "—"}</SExistingCell>
                        <SExistingCell $center aria-hidden>
                          {row.required === true ? (
                            <FaAsterisk size={12} aria-hidden className="text-red-500" />
                          ) : row.required === false ? (
                            <span className="text-gray-400">{"—"}</span>
                          ) : (
                            "—"
                          )}
                        </SExistingCell>
                      </SExistingDataRow>
                    ))
                  : null}
              </div>
            )
          })
        )}
      </SExistingBody>
    </SExistingPanel>
  )
}

const SExistingPanel = styled.div`
  position: relative;
  z-index: 50;
  width: 100%;
  box-sizing: border-box;
  margin-top: 0.25rem;
  margin-bottom: 0.5rem;
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background: white;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.08);
  overflow: visible;
`

const SExistingSearchRow = styled.div`
  flex-shrink: 0;
  padding: 0.5rem 0.75rem 0.375rem;
  background: #ffffff;
`

const SExistingHeaderSpacer = styled.div`
  min-width: 0;
`

const SExistingHeaderRow = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  display: grid;
  grid-template-columns: ${EXISTING_PICKER_GRID};
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.75rem;
  font-weight: 600;
  color: #4b5563;
`

const SExistingGroupHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.45rem 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f3f4f6;
  font-size: 0.8125rem;
  color: #111827;
  box-sizing: border-box;
`

const SExistingGroupChevron = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.125rem;
  margin: 0;
  border: none;
  border-radius: 0.25rem;
  background: transparent;
  color: #4b5563;
  cursor: pointer;
  flex-shrink: 0;
  line-height: 0;
  &:hover {
    background: rgb(0 0 0 / 0.06);
    color: #111827;
  }
`

const SExistingHeaderSortCell = styled.div<{ $center?: boolean }>`
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: ${(p) => (p.$center ? "center" : "flex-start")};
`

/** Keeps column title and sort control adjacent (not split across the cell). */
const SExistingHeaderTitleWithSort = styled.div<{ $center?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  max-width: 100%;
  justify-content: ${(p) => (p.$center ? "center" : "flex-start")};
  ${(p) => (p.$center ? "flex: 0 1 auto; margin-left: auto; margin-right: auto;" : "")}
`

const SExistingHeaderLabel = styled.span<{ $iconOnly?: boolean }>`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 0 1 auto;
  display: ${(p) => (p.$iconOnly ? "inline-flex" : "block")};
  align-items: center;
  justify-content: center;
  color: ${(p) => (p.$iconOnly ? "#4b5563" : "inherit")};
`

const SExistingSortToggle = styled.button<{ $active: boolean }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.125rem;
  margin: 0;
  border: none;
  border-radius: 0.25rem;
  background: transparent;
  color: ${(p) => (p.$active ? "#2563eb" : "#9ca3af")};
  cursor: pointer;
  line-height: 0;
  &:hover {
    color: ${(p) => (p.$active ? "#1d4ed8" : "#4b5563")};
    background: ${(p) => (p.$active ? "rgb(37 99 235 / 0.08)" : "rgb(0 0 0 / 0.04)")};
  }
`

const SExistingBody = styled.div`
  display: block;
`

const SExistingDataRow = styled.div`
  display: grid;
  grid-template-columns: ${EXISTING_PICKER_GRID};
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  text-align: left;
  border: none;
  border-bottom: 1px solid #f3f4f6;
  background: white;
  font-size: 0.8125rem;
  color: #111827;
  box-sizing: border-box;
  &:hover {
    background: #fafafa;
  }
  &:last-child {
    border-bottom: none;
  }
`

const SExistingCell = styled.div<{ $center?: boolean }>`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: ${(p) => (p.$center ? "center" : "left")};
  display: ${(p) => (p.$center ? "flex" : "block")};
  align-items: center;
  justify-content: ${(p) => (p.$center ? "center" : "flex-start")};
`

const SExistingEmpty = styled.div`
  padding: 0.75rem;
  font-size: 0.875rem;
  color: #6b7280;
`
